import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import DialogBox from './DialogBox';
import downloadCSV from '../helpers/downloadData';
import downloadExcel from '../helpers/downloadExcel';
import exportAPIsService, { ExportJobStatus } from '../hooks/exportAPIs';
import createAxiosInstance from '../utils/axiosInstance';
import Env from '../env/Env';

export interface ExportOptionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  index?: number;
  chartId: string;
  endpoint?: string;
  data?: any[];
  total?: number;
  exportFormat?: 'csv' | 'xls';
  ragColoringColumns?: number[];
  paginationInfo?: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startRecord: number;
    endRecord: number;
  } | null;
  exportActions?: {
    fetchAllDataForExport: (
      chartId: string,
      sortBy?: string,
      sortOrder?: 'asc' | 'desc',
      signal?: AbortSignal
    ) => Promise<any[] | null>;
  };
  queryParams?: Record<string, any>;
}

const QUEUE_MODE_THRESHOLD = 10000;
const POLLING_INTERVAL = 5000; // 5 seconds

const ExportOptionsDialog: React.FC<ExportOptionsDialogProps> = ({
  data,
  total,
  isOpen,
  onClose,
  chartId,
  endpoint,
  exportActions,
  paginationInfo,
  queryParams = {},
  exportFormat = 'csv',
  ragColoringColumns,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportCancelled, setExportCancelled] = useState(false);
  const [loadingBarPosition, setLoadingBarPosition] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ExportJobStatus | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportCompleted, setExportCompleted] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState<{
    signedUrl?: string;
    fileName: string;
    jobId?: string;
    data?: any[][];
    format?: 'csv' | 'xls';
  } | null>(null);
  const exportAbortController = useRef<AbortController | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCloseRef = useRef(onClose);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Calculate total rows and determine if queue mode should be used
  const totalRows = useMemo(() => {
    return total || paginationInfo?.totalRecords || 0;
  }, [total, paginationInfo]);

  const shouldUseQueueMode = useMemo(() => {
    return totalRows > QUEUE_MODE_THRESHOLD && !!endpoint;
  }, [totalRows, endpoint]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (exportAbortController.current) {
      exportAbortController.current.abort();
      exportAbortController.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsExporting(false);
    setJobId(null);
    setJobStatus(null);
    setLoadingBarPosition(0);
    setIsMinimized(false);
    setIsDownloading(false);
    setExportCompleted(false);
    setDownloadInfo(null);
  }, []);

  // Keep refs updated
  useEffect(() => {
    onCloseRef.current = onClose;
    cleanupRef.current = cleanup;
  }, [onClose, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Cleanup when dialog closes (only if not exporting or completed)
  useEffect(() => {
    if (!isOpen && !isExporting && !exportCompleted) {
      cleanup();
    }
  }, [isOpen, isExporting, exportCompleted, cleanup]);

  // Poll for job status when using queue mode
  useEffect(() => {
    if (!isExporting || !shouldUseQueueMode || !jobId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const currentJobId = jobId;
    let isPollingActive = true;

    const pollJobStatus = async () => {
      if (!isPollingActive) return;

      try {
        const status = await exportAPIsService.getJobStatus(currentJobId);
        
        if (!isPollingActive) return;

        setJobStatus(status);
        setLoadingBarPosition(status.progress || 0);

        if (status.status === 'completed') {
          if (!status.fileName) {
            isPollingActive = false;
            cleanupRef.current?.();
            alert('Export completed but file information is missing. Please contact support.');
            return;
          }

          try {
            isPollingActive = false;
            
            // Get download URL but don't download yet
            const { signedUrl, fileName: downloadFileName } = await exportAPIsService.downloadFile(status.fileName, currentJobId);
            
            // Store download info and show success state
            setDownloadInfo({
              signedUrl,
              fileName: downloadFileName || status.fileName,
              jobId: currentJobId
            });
            setExportCompleted(true);
            setIsExporting(false);
            setLoadingBarPosition(100);
            
            // Stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          } catch (error: any) {
            isPollingActive = false;
            setIsExporting(false);
            cleanupRef.current?.();
            alert(error?.message || 'Failed to prepare download. Please try again.');
          }
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          isPollingActive = false;
          cleanupRef.current?.();
          if (status.status === 'failed') {
            alert(status.error || 'Export failed. Please try again.');
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
      }
    };

    pollJobStatus();
    
    pollingIntervalRef.current = setInterval(() => {
      pollJobStatus();
    }, POLLING_INTERVAL);

    return () => {
      isPollingActive = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isExporting, shouldUseQueueMode, jobId]);

  // Simulated progress for non-queue mode
  useEffect(() => {
    if (!isExporting || shouldUseQueueMode) {
      if (!isExporting) {
        setLoadingBarPosition(0);
      }
      return;
    }

    // Simulate progress for non-queue mode exports
    const intervalId = setInterval(() => {
      setLoadingBarPosition(prev => {
        // Gradually increase progress, but stop at 99% until completion
        if (prev < 99) {
          return Math.min(prev + 2, 99);
        }
        return prev;
      });
    }, 500);

    return () => clearInterval(intervalId);
  }, [isExporting, shouldUseQueueMode]);

  const cancelExportRequest = useCallback(async () => {
    if (exportAbortController.current) {
      exportAbortController.current.abort();
      exportAbortController.current = null;
    }

    if (shouldUseQueueMode && jobId) {
      try {
        await exportAPIsService.cancelJob(jobId);
      } catch (error) {
        console.error('Failed to cancel job:', error);
      }
    }

    cleanup();
    setExportCancelled(true);
    setTimeout(() => setExportCancelled(false), 2000);
  }, [shouldUseQueueMode, jobId, cleanup]);

  const handleExportDialogClose = useCallback(() => {
    // If exporting, cancel the export
    if (isExporting) {
      cancelExportRequest();
    }
    onClose();
  }, [isExporting, cancelExportRequest, onClose]);

  const handleMinimize = useCallback(() => {
    if (isExporting) {
      setIsMinimized(true);
    }
  }, [isExporting]);

  const handleRestore = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const buildExportUrl = useCallback((endpointPath: string, params: Record<string, any>): string => {
    const apiBase = Env.API_BASE || '';
    const fullEndpoint = endpointPath.startsWith('http')
      ? endpointPath
      : `${apiBase}${endpointPath.startsWith('/') ? endpointPath : '/' + endpointPath}`;

    try {
      const url = new URL(fullEndpoint);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });
      return url.pathname + url.search;
    } catch {
      const url = new URL(fullEndpoint, window.location.origin);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });
      return url.pathname + url.search;
    }
  }, []);

  const handleFullExport = useCallback(async () => {
    const normalizedFormat: 'csv' | 'xls' = exportFormat === 'xls' ? 'xls' : 'csv';
    // Recalculate totalRows and queue mode check at export time to ensure we have latest values
    const currentTotalRows = total || paginationInfo?.totalRecords || 0;
    const shouldUseQueue = currentTotalRows > QUEUE_MODE_THRESHOLD && !!endpoint;
    
    // Add debug logging to help diagnose issues
    console.log('Export Debug:', {
      total,
      paginationInfoTotalRecords: paginationInfo?.totalRecords,
      currentTotalRows,
      endpoint,
      shouldUseQueue,
      shouldUseQueueMode, // memoized value for comparison
    });

    if (shouldUseQueue && endpoint) {
      try {
        setIsExporting(true);
        setLoadingBarPosition(0);

        const exportParams = {
          ...queryParams,
          downloadDataQueue: 'true',
          exportFormat: normalizedFormat,
          ragColumns: normalizedFormat === 'xls' && ragColoringColumns?.length
            ? ragColoringColumns.join(',')
            : undefined,
        };

        const finalUrl = buildExportUrl(endpoint, exportParams);
        const url = new URL(endpoint.startsWith('http') ? endpoint : `${Env.API_BASE || ''}${endpoint}`, window.location.origin);
        const baseURL = `${url.protocol}//${url.host}`;
        const request = createAxiosInstance(baseURL);

        const response = await request.get(finalUrl);

        if (response.data.success && response.data.jobId) {
          setJobId(response.data.jobId);
        } else {
          throw new Error(response.data.error || 'Failed to start export');
        }
      } catch (error: any) {
        console.error('Failed to trigger export:', error);
        setIsExporting(false);
        alert(error?.response?.data?.error || error?.message || 'Failed to start export. Please try again.');
      }
      return;
    }

    // Direct export for smaller datasets (non-queue mode)
    if (!exportActions?.fetchAllDataForExport) {
      // For direct export without fetchAllDataForExport, download immediately
      if (normalizedFormat === 'xls') {
        downloadExcel(data || [], chartId, { ragColoringColumns });
      } else {
        downloadCSV(data || [], chartId);
      }
      onClose();
      return;
    }

    try {
      setIsExporting(true);
      setLoadingBarPosition(0);
      exportAbortController.current = new AbortController();
      
      const allData = await exportActions.fetchAllDataForExport(
        chartId,
        undefined,
        undefined,
        exportAbortController?.current?.signal
      );

      if (exportAbortController?.current?.signal?.aborted) return;

      if (allData && allData.length > 0) {
        // Store data for download instead of auto-downloading
        setDownloadInfo({
          data: allData,
          format: normalizedFormat,
          fileName: normalizedFormat === 'xls'
            ? `${chartId}_full_export.xlsx`
            : `${chartId}_full_export.csv`,
        });
        setExportCompleted(true);
        setIsExporting(false);
        setLoadingBarPosition(100);
      } else {
        throw new Error('No data received from export service');
      }
    } catch (error: any) {
      if (error?.name === 'AbortError' || exportAbortController.current?.signal.aborted) {
        return;
      }
      console.error('Full export failed:', error);
      setIsExporting(false);
      alert(error?.message || 'Export failed. Please try again.');
    } finally {
      exportAbortController.current = null;
    }
  }, [exportFormat, total, paginationInfo, endpoint, queryParams, exportActions, chartId, data, onClose, buildExportUrl, shouldUseQueueMode, ragColoringColumns]);

  const handleExportCurrentPage = useCallback(() => {
    if (exportFormat === 'xls') {
      downloadExcel(data || [], chartId, { ragColoringColumns });
    } else {
      downloadCSV(data || [], chartId);
    }
    onClose();
  }, [data, chartId, onClose, exportFormat, ragColoringColumns]);

  const handleDownloadCompletedFile = useCallback(async () => {
    if (!downloadInfo) return;

    try {
      setIsDownloading(true);
      
      // Check if we have direct data (non-queue mode) or need to download from server (queue mode)
      if (downloadInfo.data) {
        // Non-queue mode: download directly from data
        if (downloadInfo.format === 'xls') {
          downloadExcel(
            downloadInfo.data,
            downloadInfo.fileName.replace(/\.xlsx$/i, ''),
            { ragColoringColumns }
          );
        } else {
          downloadCSV(downloadInfo.data, downloadInfo.fileName.replace('.csv', ''));
        }
      } else if (downloadInfo.signedUrl) {
        // Queue mode: Use signed URL if available
        const a = document.createElement('a');
        a.href = downloadInfo.signedUrl;
        a.download = downloadInfo.fileName;
        a.target = '_blank'; // Open in new tab as fallback
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(a);
        }, 100);
      } else if (downloadInfo.jobId) {
        // Queue mode: Fallback to stream download
        const blob = await exportAPIsService.downloadFileStream(downloadInfo.fileName, downloadInfo.jobId);
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadInfo.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      setIsDownloading(false);
      // Close dialog after successful download
      setTimeout(() => {
        cleanup();
        onClose();
      }, 500);
    } catch (error: any) {
      setIsDownloading(false);
      alert(error?.message || 'Failed to download file. Please try again.');
    }
  }, [downloadInfo, cleanup, onClose, ragColoringColumns]);

  const getStatusMessage = () => {
    if (isDownloading) {
      return 'Downloading';
    }

    if (!shouldUseQueueMode || !jobStatus) {
      return `${loadingBarPosition}%`;
    }

    switch (jobStatus.status) {
      case 'queued':
        return 'Queued';
      case 'processing':
        return `${loadingBarPosition}%`;
      case 'completed':
        return 'Done';
      case 'failed':
        return 'Failed';
      default:
        return `${loadingBarPosition}%`;
    }
  };

  // Render minimized round box (floating)
  if (isMinimized && (isExporting || exportCompleted)) {
    return (
      <>
        {exportCompleted ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadCompletedFile();
            }}
            disabled={isDownloading}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              backgroundColor: '#28a745',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDownloading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              color: 'white',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '3px solid white',
              overflow: 'hidden',
              aspectRatio: '1 / 1',
              minWidth: '70px',
              minHeight: '70px',
              maxWidth: '70px',
              maxHeight: '70px',
              padding: 0,
              margin: 0,
            }}
            title={isDownloading ? 'Downloading...' : 'Click to download file'}
          >
            <div style={{ fontSize: '9px', lineHeight: '1', textAlign: 'center' }}>
              {isDownloading ? 'Downloading' : 'Download'}
            </div>
          </button>
        ) : (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              backgroundColor: '#08305D',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              color: 'white',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '3px solid white',
              overflow: 'hidden',
              aspectRatio: '1 / 1',
              minWidth: '70px',
              minHeight: '70px',
              maxWidth: '70px',
              maxHeight: '70px',
            }}
            onClick={handleRestore}
            title="Click to restore export dialog"
          >
            <div style={{ fontSize: '11px', marginBottom: '4px', fontWeight: 'bold', lineHeight: '1', textAlign: 'center', padding: '0 4px' }}>
              {isDownloading ? 'Downloading' : getStatusMessage()}
            </div>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              animation: 'spin 1s linear infinite',
              flexShrink: 0,
            }} />
          </div>
        )}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // Prepare other actions for DialogBox (minimize button)
  const otherActions = isExporting ? [
    {
      text: 'Minimize',
      action: handleMinimize,
      style: {
        // Let SCSS handle positioning, only override colors if needed
      }
    }
  ] : undefined;

  // Render full dialog
  return (
    <DialogBox
      height={250}
      width={450}
      hideButtons={false}
      isOpen={isOpen && !isMinimized}
      onClose={handleExportDialogClose}
      otherActions={otherActions}
    >
      <div>
        <div className='chart-head'>
          <h3 className='chart-title'>📊 Export Options</h3>
        </div>

        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <p style={{
            marginBottom: '6px',
            fontSize: '13px',
            color: '#333',
            lineHeight: '1.3',
            textAlign: 'center'
          }}>
            This table contains <strong style={{ color: '#08305D' }}>
              {totalRows.toLocaleString()}
            </strong> total rows.
          </p>
          <p style={{
            fontSize: '12px',
            color: '#666',
            lineHeight: '1.2',
            textAlign: 'center'
          }}>
            Currently loaded: <strong>{data?.length || 0}</strong> rows
          </p>
        </div>

        <div className="export-options-dialog" style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {(isExporting || exportCompleted) && (
            <div style={{
              width: '100%',
              marginBottom: '10px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: `${loadingBarPosition}%`,
                  height: '100%',
                  backgroundColor: exportCompleted ? '#28a745' : '#08305D',
                  transition: 'width 0.5s ease',
                  borderRadius: '4px'
                }} />
              </div>
              <span style={{
                fontSize: '11px',
                color: exportCompleted ? '#28a745' : '#666',
                fontWeight: exportCompleted ? 'bold' : 'normal'
              }}>
                {exportCompleted ? 'Export completed successfully!' : getStatusMessage()}
              </span>
            </div>
          )}

          {exportCompleted ? (
            <button
              onClick={handleDownloadCompletedFile}
              disabled={isDownloading}
              className="export-button"
              style={{
                backgroundColor: '#28a745',
                color: 'white'
              }}
            >
              {isDownloading ? 'Downloading...' : 'Download File'}
            </button>
          ) : (
            <>
              <button
                onClick={handleFullExport}
                disabled={isExporting}
                className={`export-button ${exportCancelled ? 'export-cancelled' : ''} ${isExporting ? 'export-loading' : ''}`}
              >
                {exportCancelled
                  ? 'Export Cancelled'
                  : isExporting
                  ? 'Exporting...'
                  : `Export All Rows${exportFormat === 'xls' ? ' (XLS)' : ''}`}
              </button>
              <button
                onClick={handleExportCurrentPage}
                disabled={isExporting}
                className="export-button-secondary"
              >
                Export Current Page{exportFormat === 'xls' ? ' (XLS)' : ''}
              </button>
            </>
          )}
        </div>
      </div>
    </DialogBox>
  );
};

export default ExportOptionsDialog;
